import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { ADD_MENU_ITEM_MUTATION } from '@/contexts/menu-info-context-queries';
import { AddNewFooter } from '@/components/add-new-footer';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { ListItemSeparator } from '@/components/list-item';
import {
  MenuInfo_AddMenuItemMutation,
  MenuInfo_AddMenuItemMutationVariables,
} from '@/types';
import { MenuItemListItem } from './menu-item-list-item';
import {
  MenuItemListOption,
  useMenuInfoContext,
} from '@/contexts/menu-info-context';
import { MenuItemNameContent } from './menu-item-name-content';
import { RecyclerList } from '@/components/recycler-list';
import { SCREEN_PADDING } from '@/styles';
import {
  ScrollViewProps,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMutation } from '@apollo/client';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  listItemContainerStyleFn?: (
    option: MenuItemListOption,
  ) => StyleProp<ViewStyle>;
  onMenuItemSelect: (menuItemUUID: string) => void;
  scrollViewProps?: ScrollViewProps;
  setShowAddMenuItemSheet: React.Dispatch<React.SetStateAction<boolean>>;
  showAddMenuItemSheet: boolean;
}

export const MenuDetailComponent: React.FC<Props> = ({
  listItemContainerStyleFn,
  onMenuItemSelect,
  scrollViewProps,
  setShowAddMenuItemSheet,
  showAddMenuItemSheet,
}) => {
  const itemQueryVariables = useItemQueryVariables();
  const { loading, menuItemMap, updateCachedMenuItems } = useMenuInfoContext();
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
    user,
  } = useAuthenticatedUserContext();

  const menuItemOptions: MenuItemListOption[] = useMemo(
    () =>
      Array.from(menuItemMap.values()).map((item) => ({
        metadata: item,
        title: item.name,
        value: item.uuid,
      })),
    [menuItemMap],
  );

  const [addMenuItem, { loading: addLoading }] = useMutation<
    MenuInfo_AddMenuItemMutation,
    MenuInfo_AddMenuItemMutationVariables
  >(ADD_MENU_ITEM_MUTATION, {
    update: (client, { data }) => {
      const newMenuItem = data?.insert_menu_items_one;

      if (newMenuItem) {
        updateCachedMenuItems(client, (menuItems) => [
          ...menuItems,
          newMenuItem,
        ]);
      }
    },
  });

  return (
    <View
      style={[styles.base, { paddingBottom: Math.max(bottom, SCREEN_PADDING) }]}
    >
      {loading ? (
        <ActivityIndicator />
      ) : (
        <RecyclerList
          estimatedItemSize={110}
          ListHeaderComponent={
            isSmallLayout ? (
              <>
                <AddNewFooter
                  onSelect={() => setShowAddMenuItemSheet(true)}
                  size="small"
                  title={i18n.t('add_menu_item')}
                />
                <ListItemSeparator />
              </>
            ) : null
          }
          listItemProps={{
            listItemStyle: (option: MenuItemListOption) =>
              listItemContainerStyleFn?.(option),
            renderMainContent: (option) => (
              <MenuItemListItem onSelect={onMenuItemSelect} option={option} />
            ),
          }}
          listItemSeparatorProps={{
            style: styles.listItemSeparatorMarginHorizontal,
          }}
          sections={[
            {
              data: menuItemOptions,
              key: null,
            },
          ]}
          testID="menu-list"
          {...scrollViewProps}
        />
      )}
      {showAddMenuItemSheet ? (
        <BaseActionSheet
          isVisible
          onClose={() => setShowAddMenuItemSheet(false)}
        >
          <MenuItemNameContent
            action={i18n.t('create')}
            loading={addLoading}
            onAction={async (itemName) => {
              try {
                const addResponse = await addMenuItem({
                  variables: {
                    ...itemQueryVariables,
                    employeeUUID: user.uuid,
                    itemName,
                    restaurantUUID,
                    supplierUUID,
                  },
                });
                const newMenuItemUUID =
                  addResponse.data?.insert_menu_items_one?.uuid;

                if (newMenuItemUUID) {
                  onMenuItemSelect(newMenuItemUUID);
                }
                setShowAddMenuItemSheet(false);
              } catch {
                showErrorToast();
              }
            }}
            title={i18n.t('add_menu_item')}
          />
        </BaseActionSheet>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
    justifyContent: 'center',
  },
  listItemSeparatorMarginHorizontal: {
    marginHorizontal: 16,
  },
});
